import { useCallback } from 'react';
import {
  DEFAULT_TOAST_MESSAGE_FLOATING_ID,
  closeToastById,
  showToastMessage,
} from '@ps-refarch-ux/neon';

export interface ToastButton {
  id: string;
  text: string;
  onClick: () => void;
  type?: 'primary' | 'secondary' | 'tertiary' | 'borderless' | 'utility' | 'back-to-page';
}

export interface ToastMessage {
  heading: string;
  subheading?: string;
  /** Optional toast action buttons. */
  buttons?: ToastButton[];
  /** Skip Neon’s date row (use with a custom subheading). */
  omitTimestamp?: boolean;
}

export type ToastType = 'success' | 'error' | 'warning' | 'info';

const defaultOptions = {
  timeout: 5000,
  dismissible: true,
};

export function useToastNotifications() {
  const showToast = useCallback(
    (
      type: ToastType,
      message: ToastMessage,
      options?: { timeout?: number; dismissible?: boolean }
    ): Promise<string | undefined> | undefined => {
      try {
        const subheading =
          message.subheading !== undefined && message.subheading !== ''
            ? message.subheading
            : undefined;
        // Neon collapses regular spaces in toast headings; NBSP keeps multi-space runs intact.
        const toastHeading = message.heading.replace(/ {2,}/g, (match) =>
          '\u00a0'.repeat(match.length)
        );
        const toastMessage = {
          heading: toastHeading,
          buttons:
            message.buttons?.map((btn) => ({
              id: btn.id,
              text: btn.text,
              showText: btn.text,
              click: btn.onClick,
              type: (btn.type ?? 'secondary') as
                | 'primary'
                | 'secondary'
                | 'tertiary'
                | 'borderless'
                | 'utility'
                | 'back-to-page',
            })) ?? [],
          type,
          ...(subheading !== undefined ? { subheading } : {}),
        };
        const result = showToastMessage(toastMessage, {
          sendTo: DEFAULT_TOAST_MESSAGE_FLOATING_ID,
          autoDismiss: options?.dismissible ?? defaultOptions.dismissible,
          autoDismissTime: options?.timeout ?? defaultOptions.timeout,
          messageType: type,
        });
        return result instanceof Promise ? result : undefined;
      } catch (error) {
        console.error('showToastMessage error:', error);
        return undefined;
      }
    },
    []
  );

  return { showToast, closeToastById };
}
